import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api, { formatPrice, formatDate } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import './Admin.css';

// ── Sub-components ──────────────────────────────────────────

function StatCard({ icon, label, value, sub }) {
  return (
    <div className="stat-card card">
      <div className="stat-icon">{icon}</div>
      <div>
        <p className="stat-label">{label}</p>
        <p className="stat-value">{value}</p>
        {sub && <p className="stat-sub">{sub}</p>}
      </div>
    </div>
  );
}

// ── Products tab ────────────────────────────────────────────
function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [form, setForm] = useState({
    name: '', description: '', shortDescription: '', price: '', discountPrice: '',
    category: 'indoor-plants', stock: '', careLevel: 'beginner',
    sunlight: 'indirect-light', wateringFrequency: 'weekly',
    petFriendly: false, airPurifying: false, featured: false,
    images: [{ url: '', alt: '' }],
  });

  const load = () => {
    api.get('/products?limit=50').then(r => setProducts(r.data.products)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openCreate = () => { setEditProduct(null); setShowForm(true); };
  const openEdit = (p) => {
    setEditProduct(p);
    setForm({
      name: p.name, description: p.description, shortDescription: p.shortDescription || '',
      price: p.price, discountPrice: p.discountPrice || '', category: p.category,
      stock: p.stock, careLevel: p.careLevel || 'beginner', sunlight: p.sunlight || 'indirect-light',
      wateringFrequency: p.wateringFrequency || 'weekly',
      petFriendly: p.petFriendly, airPurifying: p.airPurifying, featured: p.featured,
      images: p.images?.length ? p.images : [{ url: '', alt: '' }],
    });
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      if (editProduct) {
        await api.put(`/products/${editProduct._id}`, form);
        toast.success('Product updated');
      } else {
        await api.post('/products', form);
        toast.success('Product created');
      }
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success('Product deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div>
      <div className="tab-header">
        <h2>Products</h2>
        <button className="btn btn-primary btn-sm" onClick={openCreate}>+ New Product</button>
      </div>

      {showForm && (
        <div className="admin-form card">
          <h3>{editProduct ? 'Edit Product' : 'New Product'}</h3>
          <form onSubmit={handleSave}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Name *</label>
                <input className="form-input" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <select className="form-input" value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))}>
                  {['indoor-plants','outdoor-plants','succulents','herbs','flowering-plants','seeds','pots-planters','tools','soil-fertilizers','watering','grow-lights','accessories'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Short Description</label>
              <input className="form-input" value={form.shortDescription} onChange={e => setForm(f => ({...f, shortDescription: e.target.value}))} />
            </div>
            <div className="form-group">
              <label className="form-label">Description *</label>
              <textarea className="form-input" rows={3} value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} required />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Price (₹) *</label>
                <input type="number" className="form-input" value={form.price} onChange={e => setForm(f => ({...f, price: e.target.value}))} required />
              </div>
              <div className="form-group">
                <label className="form-label">Discount Price (₹)</label>
                <input type="number" className="form-input" value={form.discountPrice} onChange={e => setForm(f => ({...f, discountPrice: e.target.value}))} />
              </div>
              <div className="form-group">
                <label className="form-label">Stock *</label>
                <input type="number" className="form-input" value={form.stock} onChange={e => setForm(f => ({...f, stock: e.target.value}))} required />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Image URL</label>
              <input className="form-input" value={form.images[0]?.url} onChange={e => setForm(f => ({...f, images: [{url: e.target.value, alt: f.name}]}))} placeholder="https://..." />
            </div>
            <div className="form-checkboxes">
              {[['featured', '★ Featured'], ['petFriendly', '🐾 Pet Friendly'], ['airPurifying', '💨 Air Purifying']].map(([key, label]) => (
                <label key={key} className="checkbox-label">
                  <input type="checkbox" checked={form[key]} onChange={e => setForm(f => ({...f, [key]: e.target.checked}))} />
                  {label}
                </label>
              ))}
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-ghost" onClick={() => setShowForm(false)}>Cancel</button>
              <button type="submit" className="btn btn-primary">Save Product</button>
            </div>
          </form>
        </div>
      )}

      {loading ? <div className="loading-center"><div className="spinner"></div></div> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Image</th><th>Name</th><th>Category</th><th>Price</th><th>Stock</th><th>Featured</th><th>Actions</th></tr></thead>
            <tbody>
              {products.map(p => (
                <tr key={p._id}>
                  <td><img src={p.images?.[0]?.url} alt={p.name} className="admin-thumb" /></td>
                  <td className="product-name-cell">{p.name}</td>
                  <td><span className="badge badge-green">{p.category}</span></td>
                  <td>{formatPrice(p.discountPrice || p.price)}</td>
                  <td className={p.stock < 5 ? 'low-stock' : ''}>{p.stock}</td>
                  <td>{p.featured ? '★' : '—'}</td>
                  <td>
                    <div className="action-btns">
                      <button className="btn btn-ghost btn-sm" onClick={() => openEdit(p)}>Edit</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p._id)}>Del</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Orders tab ──────────────────────────────────────────────
function OrdersTab() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/orders').then(r => setOrders(r.data.orders)).finally(() => setLoading(false));
  }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/orders/${id}/status`, { status });
      setOrders(prev => prev.map(o => o._id === id ? { ...o, orderStatus: status } : o));
      toast.success('Status updated');
    } catch { toast.error('Update failed'); }
  };

  return (
    <div>
      <div className="tab-header"><h2>All Orders</h2></div>
      {loading ? <div className="loading-center"><div className="spinner"></div></div> : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead><tr><th>Order ID</th><th>Customer</th><th>Date</th><th>Total</th><th>Payment</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {orders.map(o => (
                <tr key={o._id}>
                  <td className="mono">#{o._id.slice(-8).toUpperCase()}</td>
                  <td>{o.user?.name}<br /><small>{o.user?.email}</small></td>
                  <td>{formatDate(o.createdAt)}</td>
                  <td>{formatPrice(o.totalPrice)}</td>
                  <td><span className={o.isPaid ? 'text-green' : 'text-amber'}>{o.isPaid ? '✓ Paid' : '⏳ Pending'}</span></td>
                  <td>
                    <select
                      className="status-select"
                      value={o.orderStatus}
                      onChange={e => updateStatus(o._id, e.target.value)}
                    >
                      {['pending','processing','shipped','delivered','cancelled','refunded'].map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td><Link to={`/orders/${o._id}`} className="btn btn-ghost btn-sm">View</Link></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Admin page ──────────────────────────────────────────
export default function Admin() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!isAdmin) { navigate('/'); return; }
    api.get('/orders/stats').then(r => setStats(r.data.stats)).catch(() => {});
  }, [isAdmin, navigate]);

  if (!isAdmin) return null;

  return (
    <div className="admin-page container">
      <div className="admin-header">
        <h1 className="section-title">Admin Dashboard</h1>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        {[['dashboard', '📊 Dashboard'], ['products', '🌱 Products'], ['orders', '📦 Orders']].map(([key, label]) => (
          <button key={key} className={`admin-tab ${tab === key ? 'active' : ''}`} onClick={() => setTab(key)}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'dashboard' && stats && (
        <div>
          <div className="stats-grid">
            <StatCard icon="📦" label="Total Orders" value={stats.totalOrders} />
            <StatCard icon="💰" label="Total Revenue" value={formatPrice(stats.totalRevenue)} />
            <StatCard icon="🚚" label="Shipped" value={stats.ordersByStatus?.find(s => s._id === 'shipped')?.count || 0} />
            <StatCard icon="✅" label="Delivered" value={stats.ordersByStatus?.find(s => s._id === 'delivered')?.count || 0} />
          </div>
          <div className="card" style={{ padding: 24, marginTop: 24 }}>
            <h3 className="card-section-title">Recent Orders</h3>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Order</th><th>Customer</th><th>Total</th><th>Status</th></tr></thead>
                <tbody>
                  {stats.recentOrders?.map(o => (
                    <tr key={o._id}>
                      <td className="mono">#{o._id.slice(-8).toUpperCase()}</td>
                      <td>{o.user?.name}</td>
                      <td>{formatPrice(o.totalPrice)}</td>
                      <td><span className={`status-badge status-${o.orderStatus}`}>{o.orderStatus}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      {tab === 'products' && <ProductsTab />}
      {tab === 'orders' && <OrdersTab />}
    </div>
  );
}
